import {
  labelListResponseSchema,
  labelSchema,
  memberCandidatesResponseSchema,
  organizationOptionsResponseSchema,
  projectInvitationResponseSchema,
  projectListResponseSchema,
  projectMemberListResponseSchema,
  projectMemberSchema,
  projectSchema,
  userOptionsResponseSchema,
  workflowStatusListResponseSchema,
  workflowStatusSchema,
  type CreateLabelBody,
  type CreateProjectBody,
  type CreateStatusBody,
  type ProjectInvitationBody,
  type UpdateLabelBody,
  type UpdateProjectBody,
  type UpdateStatusBody,
} from '@pm-tool/shared';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '@/lib/api-client';

const noContent = z.unknown();

/** Query keys; everything of one project lives below `['projects', id]`. */
export const projectKeys = {
  all: ['projects'] as const,
  list: (archived: boolean) => ['projects', 'list', { archived }] as const,
  byKey: (key: string) => ['projects', 'by-key', key] as const,
  detail: (id: string) => ['projects', id] as const,
  statuses: (id: string) => ['projects', id, 'statuses'] as const,
  labels: (id: string) => ['projects', id, 'labels'] as const,
  members: (id: string) => ['projects', id, 'members'] as const,
  candidates: (id: string, q: string) => ['projects', id, 'member-candidates', q] as const,
};

export const projectListQuery = (archived: boolean) =>
  queryOptions({
    queryKey: projectKeys.list(archived),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects${archived ? '?archived=true' : ''}`,
        schema: projectListResponseSchema,
        signal,
      }),
  });

export const projectByKeyQuery = (key: string) =>
  queryOptions({
    queryKey: projectKeys.byKey(key),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/by-key/${encodeURIComponent(key)}`,
        schema: projectSchema,
        signal,
      }),
  });

export const statusesQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.statuses(projectId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/${projectId}/statuses`,
        schema: workflowStatusListResponseSchema,
        signal,
      }),
  });

export const labelsQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.labels(projectId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/${projectId}/labels`,
        schema: labelListResponseSchema,
        signal,
      }),
  });

export const membersQuery = (projectId: string) =>
  queryOptions({
    queryKey: projectKeys.members(projectId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/${projectId}/members`,
        schema: projectMemberListResponseSchema,
        signal,
      }),
  });

export const memberCandidatesQuery = (projectId: string, q: string) =>
  queryOptions({
    queryKey: projectKeys.candidates(projectId, q),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/${projectId}/member-candidates?${new URLSearchParams({ q }).toString()}`,
        schema: memberCandidatesResponseSchema,
        signal,
      }),
  });

export const organizationOptionsQuery = queryOptions({
  queryKey: ['options', 'organizations'],
  queryFn: ({ signal }) =>
    apiRequest({
      path: '/v1/options/organizations',
      schema: organizationOptionsResponseSchema,
      signal,
    }),
});

export const internalUserOptionsQuery = (q: string) =>
  queryOptions({
    queryKey: ['options', 'internal-users', q],
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/options/internal-users?${new URLSearchParams({ q }).toString()}`,
        schema: userOptionsResponseSchema,
        signal,
      }),
  });

export const projectApi = {
  create: (body: CreateProjectBody) =>
    apiRequest({ method: 'POST', path: '/v1/projects', schema: projectSchema, body }),
  update: (id: string, body: UpdateProjectBody) =>
    apiRequest({ method: 'PATCH', path: `/v1/projects/${id}`, schema: projectSchema, body }),
  archive: (id: string) =>
    apiRequest({ method: 'POST', path: `/v1/projects/${id}/archive`, schema: projectSchema }),
  unarchive: (id: string) =>
    apiRequest({ method: 'POST', path: `/v1/projects/${id}/unarchive`, schema: projectSchema }),

  createStatus: (projectId: string, body: CreateStatusBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/projects/${projectId}/statuses`,
      schema: workflowStatusSchema,
      body,
    }),
  updateStatus: (projectId: string, statusId: string, body: UpdateStatusBody) =>
    apiRequest({
      method: 'PATCH',
      path: `/v1/projects/${projectId}/statuses/${statusId}`,
      schema: workflowStatusSchema,
      body,
    }),
  deleteStatus: (projectId: string, statusId: string, targetStatusId?: string) =>
    apiRequest({
      method: 'DELETE',
      path: `/v1/projects/${projectId}/statuses/${statusId}${
        targetStatusId === undefined ? '' : `?targetStatusId=${targetStatusId}`
      }`,
      schema: noContent,
    }),

  createLabel: (projectId: string, body: CreateLabelBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/projects/${projectId}/labels`,
      schema: labelSchema,
      body,
    }),
  updateLabel: (projectId: string, labelId: string, body: UpdateLabelBody) =>
    apiRequest({
      method: 'PATCH',
      path: `/v1/projects/${projectId}/labels/${labelId}`,
      schema: labelSchema,
      body,
    }),
  deleteLabel: (projectId: string, labelId: string) =>
    apiRequest({
      method: 'DELETE',
      path: `/v1/projects/${projectId}/labels/${labelId}`,
      schema: noContent,
    }),

  setMember: (projectId: string, userId: string, roleId: string) =>
    apiRequest({
      method: 'PUT',
      path: `/v1/projects/${projectId}/members/${userId}`,
      schema: projectMemberSchema,
      body: { roleId },
    }),
  removeMember: (projectId: string, userId: string) =>
    apiRequest({
      method: 'DELETE',
      path: `/v1/projects/${projectId}/members/${userId}`,
      schema: noContent,
    }),
  /** E-33: invite a customer of the project organization (or re-invite, or add an active one). */
  inviteCustomer: (projectId: string, body: ProjectInvitationBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/projects/${projectId}/invitations`,
      schema: projectInvitationResponseSchema,
      body,
    }),
};
